import { useState, useEffect } from "react";
import { channelSearch } from "../chat-services/channelSearch";
import { messageSearch } from "../chat-services/messageSearch";
import { useDebounce } from "./useDebounce";
/**
 * Custom hook to perform a unified search across channels and messages in a chat client.
 * It combines results from both channel and message searches, allowing for efficient searching.
 *
 * @param {Object} chatClient - The chat client instance to perform searches on.
 * @param {Object} options - Optional parameters for search, such as offset and limit.
 * @returns {Object} - Contains search query, results, loading state, and a function to set the search query.
 */
export const useUnifiedChannelSearch = (chatClient, options) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 300);
  const mergeResults = (channels, messages) => {
    const results = new Map();
    channels.forEach(channel => {
      results.set(channel.cid, {
        channel,
        matchType: "user",
        relevantMessages: []
      });
    });
    messages.forEach(msgResult => {
      const channel = msgResult.message.channel;
      if (results.has(channel.cid)) {
        results.get(channel.cid).relevantMessages.push(msgResult.message);
        results.get(channel.cid).matchType = "message";
      } else {
        results.set(channel.cid, {
          channel,
          matchType: "message",
          relevantMessages: [msgResult.message]
        });
      }
    });
    return Array.from(results.values());
  };

  const paginateResults = (results, options, defaultLimit = 25) => {
    const offset = Number.isFinite(options?.offset) ? options.offset : 0;
    const limit = Number.isFinite(options?.limit) ? options.limit : defaultLimit;
    if (offset < 0 || limit <= 0) return [];
    return results.slice(offset, offset + limit);
  };

  useEffect(() => {
    const performSearch = async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }
      setLoading(true);
      try {
        const channels = await channelSearch(chatClient, query, chatClient.userID, options);
        const messages = await messageSearch(chatClient, query, chatClient.userID, options);
        const mergedResults = mergeResults(channels, messages);
        setSearchResults(paginateResults(mergedResults, options));
      } catch (error) {
        console.error("Unified search error:", error);
        setSearchResults([]);
      }
      setLoading(false);
    };
    performSearch(debouncedQuery);
  }, [debouncedQuery, chatClient, options]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    loading
  };
};